// Bounded, prefetching image cache for the secpose viewer.
//
// secpose frames are discrete JPGs proxied by the worker behind Bearer auth, so
// they can't be loaded via a plain <img src> (no auth header) and the platform's
// video-frame extractor (videoFramePrefetcher) doesn't apply (that decodes a
// downloaded video, not discrete images). This mirrors that wheel's STRATEGY —
// a sliding prefetch window + LRU eviction with URL.revokeObjectURL — so a weak
// machine never accumulates the whole episode (100 frames × 4 cameras ≈ 140MB)
// in memory.

import { isOidcConfigured, userManager } from '../oidc';

// ~48 × ~360KB ≈ 17MB of compressed blobs held at once. The 4 currently-visible
// cameras + a small scrub window fit comfortably; everything older is revoked.
const MAX_CACHED = 48;

const cache = new Map<string, string>(); // url → objectURL (insertion order = LRU)
const inflight = new Map<string, Promise<string>>();

async function fetchObjectUrl(url: string): Promise<string> {
  const headers: Record<string, string> = {};
  if (isOidcConfigured) {
    const user = await userManager.getUser();
    if (user?.access_token) headers.Authorization = `Bearer ${user.access_token}`;
  }
  const resp = await fetch(url, { headers });
  if (!resp.ok) throw new Error(`secpose image ${resp.status}`);
  return URL.createObjectURL(await resp.blob());
}

function evict(): void {
  while (cache.size > MAX_CACHED) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    const obj = cache.get(oldest);
    cache.delete(oldest);
    if (obj) URL.revokeObjectURL(obj);
  }
}

/** Authed fetch → object URL, cached. In-flight requests are deduped. */
export async function loadSecPoseImage(url: string): Promise<string> {
  const hit = cache.get(url);
  if (hit) {
    // Refresh LRU recency.
    cache.delete(url);
    cache.set(url, hit);
    return hit;
  }
  const pending = inflight.get(url);
  if (pending) return pending;

  const p = fetchObjectUrl(url)
    .then((obj) => {
      inflight.delete(url);
      cache.set(url, obj);
      evict();
      return obj;
    })
    .catch((err) => {
      inflight.delete(url);
      throw err;
    });
  inflight.set(url, p);
  return p;
}

/** Fire-and-forget warm of an image into the cache (for the scrub window). */
export function prefetchSecPoseImage(url: string | undefined): void {
  if (!url || cache.has(url) || inflight.has(url)) return;
  void loadSecPoseImage(url).catch(() => {});
}

/** Revoke and drop everything — call on episode change / viewer unmount. */
export function clearSecPoseImageCache(): void {
  for (const obj of cache.values()) URL.revokeObjectURL(obj);
  cache.clear();
  inflight.clear();
}
